import React, { useMemo } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { Visibility as ViewIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
    PtiAssetType,
    PreTripInspectionListItemDto,
    useGetPtiPaginatedQuery,
} from "../../../api/ptiApi";
import { useGetTrailersQuery } from "../../../api/trailersApi";
import { useGetUsersPaginatedWithSearchQuery } from "../../../api/usersApi";
import { useGetVehiclesQuery } from "../../../api/vehiclesApi";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../../constants/dataTable";
import { UserRoles } from "../../../constants/roles";
import {
    Action,
    Column,
    DataTable,
    createColumn,
    makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";
import { useDataTablePagination } from "../../../hooks/useDataTablePagination";
import { getPtiStatusLabel, PTI_STATUS_FILTER_OPTIONS, getPtiAssetDisplayLabel, PTI_STATUS } from "../ptiConstants";

interface PtiManagementListProps {
    assetType: PtiAssetType;
}

const PtiManagementList: React.FC<PtiManagementListProps> = ({ assetType }) => {
    const navigate = useNavigate();
    const { data: vehicles = [] } = useGetVehiclesQuery();
    const { data: trailers = [] } = useGetTrailersQuery();
    const { data: driversData } = useGetUsersPaginatedWithSearchQuery({
        pageNumber: 1,
        pageSize: 500,
        filters: { role: UserRoles.TRUCK_DRIVER },
    });

    const assetFilterKey = assetType === "Vehicle" ? "vehicleId" : "trailerId";
    const assetColumnLabel = assetType === "Vehicle" ? "Vehicle" : "Trailer";
    const assetOptions =
        assetType === "Vehicle"
            ? vehicles
                  .map((vehicle) => {
                      const unit = vehicle.registrationNumber;
                      const label =
                          unit && vehicle.name
                              ? `${unit} · ${vehicle.name}`
                              : unit || vehicle.name || `Vehicle ${vehicle.id}`;
                      return { value: vehicle.id, label };
                  })
                  .sort((a, b) => a.label.localeCompare(b.label))
            : trailers
                  .filter((trailer) => !trailer.isArchived)
                  .map((trailer) => ({
                      value: trailer.id,
                      label: trailer.unitNumber || `Trailer ${trailer.id}`,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label));

    const driverOptions =
        driversData?.items.map((driver) => ({
            value: driver.id,
            label: `${driver.firstName} ${driver.lastName}`.trim(),
        })) ?? [];

    const {
        items: inspections,
        isLoading,
        isLoadingRows,
        isLoadingMore,
        totalCount,
        hasNextPage,
        handleSearchChange,
        handleLoadMore,
    } = useDataTablePagination(useGetPtiPaginatedQuery, DEFAULT_DATA_TABLE_PAGE_SIZE, {
        baseFilters: { assetType },
    });

    const columns: Column[] = useMemo(
        () => [
            createColumn("inspectionDate", "Inspection Date", {
                renderCell: (value: string) => format(new Date(value), "MMM d, yyyy h:mm a"),
            }),
            createColumn(assetFilterKey, assetColumnLabel, {
                renderCell: (_value: unknown, row: PreTripInspectionListItemDto) =>
                    getPtiAssetDisplayLabel(row),
            }),
            createColumn("submittedByName", "Driver"),
            createColumn("status", "Status", {
                renderCell: (value: string) => (
                    <Chip
                        size="small"
                        label={getPtiStatusLabel(value)}
                        color={
                            value === "Completed"
                                ? "success"
                                : value === "Rejected"
                                  ? "error"
                                  : "warning"
                        }
                        variant="outlined"
                    />
                ),
            }),
            createColumn("photos", "Photos", {
                renderCell: (_value: unknown, row: PreTripInspectionListItemDto) => (
                    <Chip
                        size="small"
                        label={`${row.photoCount}/${row.totalItemCount}`}
                        variant="outlined"
                    />
                ),
            }),
            createColumn("measurementsComplete", "Measurements", {
                renderCell: (value: boolean) => (
                    <Chip
                        size="small"
                        label={value ? "Complete" : "Incomplete"}
                        color={value ? "success" : "default"}
                        variant="outlined"
                    />
                ),
            }),
            createColumn("reviewedByName", "Reviewed By", {
                renderCell: (value: string | null) => value || "—",
            }),
        ],
        [assetColumnLabel, assetFilterKey]
    );

    const searchableColumns: SearchableColumn[] = useMemo(
        () => [
            ...makeColumnsSearchable(columns, ["submittedByName"], {
                submittedByName: "string",
            }),
            {
                field: assetFilterKey,
                headerName: assetColumnLabel,
                searchable: true,
                searchType: "select",
                selectOptions: assetOptions,
            },
            {
                field: "status",
                headerName: "Status",
                searchable: true,
                searchType: "select",
                selectOptions: PTI_STATUS_FILTER_OPTIONS,
            },
            {
                field: "submittedByUserId",
                headerName: "Driver",
                searchable: true,
                searchType: "select",
                selectOptions: driverOptions,
            },
        ],
        [assetColumnLabel, assetFilterKey, assetOptions, columns, driverOptions]
    );

    const getActions = (inspection: PreTripInspectionListItemDto): Action<PreTripInspectionListItemDto>[] => [
        {
            icon: <ViewIcon fontSize="small" />,
            label: inspection.status === PTI_STATUS.Submitted ? "Review" : "View",
            onClick: () => navigate(`/pti/${inspection.id}`),
            color: "primary",
        },
    ];

    if (isLoading && inspections.length === 0) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <DataTable<PreTripInspectionListItemDto>
                items={inspections}
                columns={columns}
                actions={getActions}
                isLoading={isLoading}
                title={`${assetType} PTIs`}
                totalCount={totalCount}
                hasNextPage={hasNextPage}
                isLoadingMore={isLoadingMore}
                isLoadingRows={isLoadingRows}
                onLoadMore={handleLoadMore}
                enableSearch
                searchableColumns={searchableColumns}
                onSearchChange={handleSearchChange}
            />
        </>
    );
};

export default PtiManagementList;
